/*!
 * ports.js — what each kind of cue can publish and what it can be driven on
 * © 2025 Rob Canning — GPLv3
 *
 * The connections view uses this to show every port a score COULD use, not
 * only the ones it does, so opening it on a score with no bindings still shows
 * what is possible.
 *
 * ── THIS TABLE IS HAND-MAINTAINED, AND THAT IS ITS WEAKNESS ────────────────
 * Nothing derives it, so a new cue parameter that binds, or a new published
 * channel, will not appear here until someone adds it — and a port listed here
 * that does not really work is worse than one that is missing, because the
 * view would be inviting a connection that silently does nothing.
 *
 * So the rule is: an entry earns its place by a line of code.
 *   outs — a publish("<kind>", uid, {…}) call, with those exact channel keys
 *   ins  — a parameter that actually reaches bindParam / setupSpeedBinding /
 *          setupParamBinding in that cue's module
 *
 * Every entry below names the call site it comes from. If you add a port,
 * name yours too. Verified against the tree at the time of writing; the
 * measured survey is in notes/control-plane-inventory.md §B.
 */

/** @typedef {{ outs: Record<string,string>, ins: Record<string,string> }} PortSpec */

/** Channels are described, not just listed — the view shows these on hover. */
export const PORTS = {
  // rotate.js:426, :619 publish angle/rad/norm and vel/tvel via
  // velocityChannels(); dur binds at :206, :559
  rotate: {
    moves: true,
    outs: { angle: "degrees, 0-360", rad: "radians", norm: "0-1 per revolution",
            vel: "angular speed, deg/s", tvel: "turns per second" },
    ins:  { dur: "seconds per revolution" },
  },

  // scale.js:617, :732 publish sx/sy/uniform; sx and sy bind at :792, :804,
  // dur at :550
  scale: {
    moves: true,
    outs: { sx: "x scale factor", sy: "y scale factor", uniform: "mean of the two" },
    ins:  { sx: "x scale factor", sy: "y scale factor", dur: "seconds per step" },
  },

  // color.js:356, :547 publish hNorm/sNorm/lNorm; dur binds at :318, :467
  color: {
    outs: { hNorm: "hue, 0-1", sNorm: "saturation, 0-1", lNorm: "lightness, 0-1" },
    ins:  { dur: "seconds per cycle" },
  },

  // fade.js:278, :282, :366 publish opacity; from/to/dur sample a follow() at
  // :192 — read when the cue fires, not followed (a fade's ramp is built once)
  fade: {
    outs: { opacity: "0-1" },
    ins:  { from: "start opacity, sampled at trigger",
            to: "end opacity, sampled at trigger",
            dur: "seconds, sampled at trigger" },
  },

  // o2p.js:1067, :1225, :2021, :2327 publish t/x/y/angle/p, the moving
  // sites also vel/vx/vy/tvel via velocityChannels(); dur binds at
  // :1796, rotspeed at :1809
  o2p: {
    moves: true,
    outs: { t: "how far along the path, 0-1", x: "x position, 0-1",
            y: "y position, 0-1", angle: "heading in degrees",
            p: "rotation handle, 0-1",
            vel: "speed, score units/s", vx: "x velocity, score units/s",
            vy: "y velocity, score units/s",
            tvel: "path lengths (laps) per second — the rate of t" },
    ins:  { dur: "seconds for one traverse", rotspeed: "handle rotation speed" },
  },

  // oscCtrl.js:183
  oscCtrl: {
    outs: { t: "position along the lane, 0-1", v: "lane value, 0-1" },
    ins:  {},
  },

  // controlXY.js:511
  controlXY: {
    outs: { x: "pad x, 0-1", y: "pad y, 0-1", p: "handle rotation, 0-1" },
    ins:  {},
  },

  // synth.js:1252, :1278, :1295, :1315, :1331 — bound per voice
  synth: {
    outs: {},
    ins:  { freq: "pitch in Hz", amp: "level, 0-1", pan: "-1 to 1",
            cutoff: "filter frequency in Hz", q: "filter resonance" },
  },

  // audioFile.js:1573 (steer) / :1583 — a playing voice is steered through
  // its update(): takeFollows + steerFollows in audioShared.js
  audio: {
    outs: {},
    ins:  { amp: "level, 0-1", pan: "-1 to 1", speed: "playback rate (1 = as recorded)" },
  },

  // audioPool.js:326 — each shot reads its source as it fires (valueNow)
  audioPool: {
    outs: {},
    ins:  { amp: "level, 0-1, read at each shot", pan: "-1 to 1, read at each shot",
            speed: "playback rate, read at each shot" },
  },

  // audioImpulse.js:511 + steerImpulse (:137) steer rate/jitter; each hit
  // reads amp/pan/speed as it fires (:235)
  audioImpulse: {
    outs: {},
    ins:  { rate: "hits per minute", jitter: "0 steady … 1 random",
            amp: "level, 0-1, read at each hit", pan: "-1 to 1, read at each hit",
            speed: "playback rate, read at each hit" },
  },

  // image.js:361 publishes index/t; opacity binds at :268, index at :422
  image: {
    outs: { index: "which plate, 0-based", t: "position through the folder, 0-1" },
    ins:  { opacity: "0-1", index: "pick the plate by position in the folder" },
  },

  // trans.js:300 publishes t/legT/x/y and vel/vx/vy/tvel. It MOVES too,
  // so it can also fire another cue by passing over it (probeTriggers.js) —
  // the thing that does most of the triggering in practice.
  trans: {
    moves: true,
    outs: { t: "progress through the whole waypoint list, 0-1",
            legT: "progress along the current leg, 0-1",
            x: "x across the travel, 0-1", y: "y across the travel, 0-1",
            vel: "speed, score units/s", vx: "x velocity, score units/s",
            vy: "y velocity, score units/s",
            tvel: "the rate of t: whole waypoint lists per second" },
    ins:  {},
  },

  // preProcessDrag.js:311 publishes x/y and vel/vx/vy. Moved by hand, so it
  // can fire things too — and it is the most direct source a performer has.
  drag: {
    moves: true,
    outs: { x: "where it sits across the score, 0-1 (clamped)",
            y: "and down it, 0-1 (clamped)",
            vel: "speed, score units/s", vx: "x velocity, score units/s",
            vy: "y velocity, score units/s" },
    ins:  {},
  },

  // text.js:19 samples a follow() for the ticker speed
  text: {
    outs: {},
    ins:  { speed: "scroll speed in px/s, sampled at trigger" },
  },

  // ui.js:27 samples a follow() for each of these at trigger — a ui() cue is
  // a command carried out by a CSS transition, not a value to follow.
  ui: {
    outs: {},
    ins:  { opacity: "0-1, sampled at trigger", scale: "scale factor, sampled",
            rotate: "degrees, sampled", x: "pixels, sampled", y: "pixels, sampled",
            dur: "seconds of transition, sampled" },
  },

  // RAF.js publish("transport", …) — a system source with no element behind it.
  // The uid IS "transport": follow(transport.t, …) in any score.
  transport: {
    outs: { t: "the playhead through the score, 0-1",
            elapsed: "seconds into the score",
            speed: "the transport's speed multiplier",
            playing: "1 while the transport runs" },
    ins:  {},
  },

  // timers.js publish("stopwatch", …) gives elapsed, and t/remaining when the
  // cue has a hold; endHold() sets <uid>.end once the hold has run
  stopwatch: {
    outs: { elapsed: "seconds since it started",
            t: "through its hold, 0-1", remaining: "seconds left of the hold",
            end: "1 once its hold has run" },
    ins:  {},
  },

  // video.js:177 publishes time/t; opacity binds at :441, speed at :451
  video: {
    outs: { time: "playback position in seconds", t: "through the file, 0-1" },
    ins:  { opacity: "0-1", speed: "playback rate" },
  },

  // metro.js:249 publishes beat/phase/bar from the grid clock
  metro: {
    outs: { beat: "which beat of the bar, 1-based", phase: "through the bar, 0-1",
            bar: "bars since it started" },
    ins:  {},
  },

  // random.js — a dice roll as a source. It has no inlets: what makes it roll
  // is its trig:, which is a trigger edge rather than a bound parameter.
  random: {
    outs: { value: "the roll, in the min..max range asked for",
            norm: "the same roll as 0-1" },
    ins:  {},
  },

  // audioAnalysis.js:317 — started by the adc() cue, published under its uid
  // ("adc" unless the cue renames it). Named after Pd/Max's adc~.
  adc: {
    outs: { amp: "level", peak: "peak level", centroid: "spectral centroid",
            onset: "onset detection", low: "low band", mid: "mid band",
            high: "high band", pitch: "detected pitch in Hz",
            note: "detected note number", pitchConf: "pitch confidence, 0-1" },
    ins:  {},
  },

  // liveSignals.js -- the pointer, anywhere in the window
  mouse: {
    outs: { x: "across the window, 0-1", y: "down the window, 0-1", down: "1 while pressed",
            hit: "a press", n: "presses counted" },
    ins:  {},
  },

  // cam.js -- the camera, published under its uid ("cam" unless named)
  cam: {
    outs: { motion: "share of the picture moving, 0-1", level: "movement against its most lately, 0-1", bright: "average brightness, 0-1",
            x: "where the movement is, across, 0-1", y: "where the movement is, down, 0-1",
            hit: "a burst of movement", n: "bursts counted" },
    ins:  {},
  },
};

/** Cue name as written in the DSL → key in PORTS. */
const ALIAS = {
  scalexy: "scale",
  metronome: "metro",
  audiopool: "audioPool",
  audioimpulse: "audioImpulse",
};

/** The cue name at the head of a DSL id: "rotate(uid:r1, …)" → "rotate". */
export function cueNameOf(id) {
  const m = /^\s*([a-zA-Z][a-zA-Z0-9]*)\s*\(/.exec(String(id ?? ""));
  if (!m) return null;
  const name = m[1];
  return ALIAS[name.toLowerCase()] ?? name;
}

/**
 * Ports for a cue name, or null if that kind has none worth showing.
 * A cue that moves gets a "fires" outlet whatever else it has: any object
 * passing over another triggers it, and that is a connection like any other.
 */
export function portsFor(cueName) {
  if (!cueName) return null;
  const spec = PORTS[cueName] ?? PORTS[ALIAS[String(cueName).toLowerCase()] ?? ""];
  if (!spec) return null;
  const outs = Object.keys(spec.outs), ins = Object.keys(spec.ins);
  if (!outs.length && !ins.length && !spec.moves) return null;
  return spec;
}

/** Outlet names for a cue, "fires" included when the object can move. */
export function outletsFor(cueName) {
  const spec = portsFor(cueName);
  if (!spec) return [];
  const names = Object.keys(spec.outs);
  if (spec.moves) names.push("fires");
  return names;
}

/** Inlet names for a cue. Anything can BE triggered, so every cue takes "fires". */
export function inletsFor(cueName) {
  const spec = portsFor(cueName);
  if (!spec) return [];
  return [...Object.keys(spec.ins), "fires"];
}

/** One-line description of a channel or parameter, for the hover label. */
export function describePort(cueName, dir, name) {
  if (name === "fires") {
    return dir === "out" ? "fires a cue it passes over" : "fired by an object passing over";
  }
  const spec = portsFor(cueName);
  return spec?.[dir === "out" ? "outs" : "ins"]?.[name] ?? "";
}

window.oscillaPorts = { PORTS, portsFor, outletsFor, inletsFor, cueNameOf, describePort };
